import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';

import setNullEmptyValues from 'common/util/setNullEmptyValues';
import { stateOptionItems } from 'common/util/stateCodes';
import { countryOptionItems } from 'common/util/countryCodes';

import {
  SR_API_ACTION_FUZZY_SEARCH_ADDRESS_STREET_SUFFIX,
  SR_API_ACTION_APARTMENT_BUILDING_UPSERT,
} from 'api/actionTypes';

import { createInitialValues, createFormValidationFn } from 'client/helpers/formikHelpers';

import SrBox from 'client/components/SrBox';
import SrFlex from 'client/components/SrFlex';
import SrText from 'client/components/SrText';
import ButtonDeprecated from 'client/components/ButtonDeprecated';
import SrDarkModal from 'client/components/SrDarkModal';
import SrModalContent from 'client/components/SrModalContent';
import SrFormikField, { SR_FIELD_THEME_ADMIN } from 'client/components/SrFormikField';

import postWithState from 'client/actions/postWithState';
import log from 'client/lib/log';

function getApartmentBuildingFormFields({ streetSuffixMatches, requestStreetAutocompleteChoices }) {
  return [
    {
      id: 'apartmentId',
      type: 'hidden',
    },
    {
      id: 'addressStreetNumber',
      type: 'short_text',
      title: 'street number',
      validations: ['required'],
    },
    {
      id: 'addressStreetName',
      type: 'short_text',
      title: 'street name',
      validations: ['required'],
    },
    {
      id: 'addressStreetSuffix',
      title: 'street suffix',
      type: 'autocomplete',
      properties: {
        placeholder: 'Rd',
        choices: streetSuffixMatches,
        onSuggestionsFetchRequested: requestStreetAutocompleteChoices,
        minCharMatchCount: 1,
      },
    },
    {
      id: 'addressZip',
      type: 'short_text',
      title: 'zip',
      validations: ['required', 'zipCode'],
    },
    {
      id: 'addressCity',
      type: 'short_text',
      title: 'city',
      validations: ['required'],
    },
    {
      id: 'addressStateCode',
      title: 'state',
      type: 'select',
      properties: {
        choices: stateOptionItems,
      },
      validations: ['required'],
    },
    {
      id: 'addressCountryCode',
      title: 'country',
      type: 'select',
      properties: {
        choices: countryOptionItems,
      },
      validations: ['required'],
    },
    {
      id: 'locationLatitude',
      title: 'location latitude',
      type: 'number',
    },
    {
      id: 'locationLongitude',
      title: 'location longitude',
      type: 'number',
    },
    {
      id: 'hasLeasingOffice',
      title: 'has a leasing office?',
      type: 'yes_no',
      validations: ['required'],
    },
  ];
}

class SrApartmentBuildingModal extends Component {
  static propTypes = {
    apartmentBuilding: PropTypes.object.isRequired,
    onClose: PropTypes.func.isRequired,
    onChange: PropTypes.func.isRequired,
    dispatch: PropTypes.func.isRequired,
  };

  constructor(props) {
    super(props);

    this.state = {
      streetSuffixMatches: [],
    };

    this._isUnmounting = false;
  }

  componentWillUnmount() {
    this._isUnmounting = true;
  }

  _onRequestStreetSuffixAutocompleteChoices = ({ value }) => {
    const { dispatch } = this.props;

    dispatch(postWithState, {
      action: SR_API_ACTION_FUZZY_SEARCH_ADDRESS_STREET_SUFFIX,
      payload: {
        value,
      },
    })
      .then((streetSuffixMatches) => {
        if (!this._isUnmounting && streetSuffixMatches) {
          this.setState({ streetSuffixMatches });
        }
      })
      .catch(log.error);
  };

  _onSubmitForm = (formValues) => {
    const { apartmentBuilding, onChange, dispatch } = this.props;
    dispatch(postWithState, {
      action: SR_API_ACTION_APARTMENT_BUILDING_UPSERT,
      payload: {
        id: apartmentBuilding.id,
        ...setNullEmptyValues(formValues),
      },
    }).then(onChange);
  };

  render() {
    const { streetSuffixMatches } = this.state;
    const { apartmentBuilding, onClose } = this.props;
    const apartmentBuildingFormFields = getApartmentBuildingFormFields({
      streetSuffixMatches: streetSuffixMatches,
      requestStreetAutocompleteChoices: this._onRequestStreetSuffixAutocompleteChoices,
    });

    return (
      <SrDarkModal onClose={onClose}>
        <SrModalContent style={{ maxWidth: 440, height: '94vh', marginTop: 4 }}>
          <SrFlex justify="center">
            <SrText size="2xl" align="center" font="whitney-sc">
              apartment building
            </SrText>
          </SrFlex>
          <SrBox marginTop={20}>
            <Formik
              initialValues={createInitialValues({
                formFields: apartmentBuildingFormFields,
                defaultValues: apartmentBuilding,
              })}
              validate={createFormValidationFn(apartmentBuildingFormFields)}
              onSubmit={this._onSubmitForm}
              render={(formikBag) => {
                return (
                  <Form autoComplete="off">
                    {apartmentBuildingFormFields.map((formField) => (
                      <SrFormikField
                        key={formField.id}
                        formField={formField}
                        formikBag={formikBag}
                        className={SR_FIELD_THEME_ADMIN}
                      />
                    ))}
                    <SrBox marginTop={20}>
                      <ButtonDeprecated type="submit" disabled={!formikBag.isValid}>
                        update
                      </ButtonDeprecated>
                    </SrBox>
                  </Form>
                );
              }}
            />
          </SrBox>
        </SrModalContent>
      </SrDarkModal>
    );
  }
}

export default SrApartmentBuildingModal;
